import { useEffect, useState } from 'react'
import { getToken } from 'firebase/app-check'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { appCheck } from '@/lib/firebase'

/**
 * Hands Firebase App Check tokens to the Maps JS SDK before the map mounts.
 * Required while maps-backend.googleapis.com App Check is ENFORCED.
 */
export function useMapsAppCheckReady(): boolean {
  const coreLib = useMapsLibrary('core')
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!coreLib) return
    let cancelled = false

    ;(async () => {
      const check = appCheck
      if (!check) {
        console.warn(
          '[maps] App Check is not initialized — Maps may fail while maps-backend App Check is enforced.',
        )
        if (!cancelled) setReady(true)
        return
      }

      coreLib.Settings.getInstance().fetchAppCheckToken = () =>
        getToken(check, /* forceRefresh */ false)

      try {
        // Warm a token so the first map tile request is not racing App Check.
        await getToken(check, false)
      } catch (e) {
        console.error(
          '[maps] Failed to get App Check token. Confirm VITE_APPCHECK_DEBUG_TOKEN is registered ' +
            'for the "Biddle Web App" app in Firebase Console → App Check → Manage debug tokens.',
          e,
        )
      }

      if (!cancelled) setReady(true)
    })()

    return () => {
      cancelled = true
    }
  }, [coreLib])

  return ready
}

/** Mount once under the shared `MapsProvider` so App Check is wired early. */
export function MapsAppCheckBootstrap() {
  useMapsAppCheckReady()
  return null
}
